import React from 'react'
import { Redirect, Router as BaseRouter, Switch } from 'react-router'
import { createBrowserHistory } from 'history'

import OnlyAuthorized from 'components/HOC/OnlyAuthorized'
import OnlyNotAuthorized from 'components/HOC/OnlyNotAuthorized'
import ScreenRoute from 'components/ScreenRoute'

import LeaderboardRoute from './Leaderboard'
import GameRoute from './Game'
import GamesRoute from './Games'
import FriendsRoute from './Friends'
import InviteMoreFriendsRoute from './InviteMoreFriends'
import SelectGameTypeRoute from './SelectGameType'
import SelectMatchRoute from './SelectMatch'
import SelectFriendsRoute from './SelectFriends'
import SelectPlayersRoute from './SelectPlayers'
import SubPlayersRoute from './SubPlayers'
import AcceptInviteRoute from './AcceptInvite'
import SelectStakeRoute from './SelectStake'
import SignInRoute from './SignIn'
import SignUpRoute from './SignUp'
import KickerLandingRoute from './KickerLanding'
import KickerSignInRoute from './KickerSignIn'
import TutorialRoute from './Tutorial'
import WelcomeRoute from './Welcome'
import StoreRoute from './Store'
import ProfileRoute from './Profile'
import EventsRoute from './Events'
import EventRoute from './Event'
import SettingsRoute from './Settings'
import ResetPasswordRoute from './ResetPassword'
import RulesRoute from './Rules'
import PremiumAccountsRoute from './PremiumAccounts'
import LandingRoute from './Landing'
import NotFoundRoute from './NotFound'
import SelectRoundRoute from './SelectRound'
import SelectClassicPlayersRoute from './SelectClassicPlayers'

import config from 'config'

const GuardedRoute = OnlyAuthorized<React.ComponentProps<typeof ScreenRoute>>(
  (props) => <ScreenRoute {...props} />,
  <Redirect to="/landing" />,
)

const GuestRoute = OnlyNotAuthorized<React.ComponentProps<typeof ScreenRoute>>(
  (props) => <ScreenRoute {...props} />,
  <Redirect to="/" />,
)

export const history = createBrowserHistory()

const Routes = () => (
  <BaseRouter history={history}>
    <Switch>
      <Redirect exact path="/" to="/games" />
      <Redirect exact path="/games/create" to="/games/create/type" />
      <Redirect exact path="/ranking" to="/ranking/global/winnings" />
      <Redirect exact path="/ranking/global" to="/ranking/global/winnings" />
      <Redirect exact path="/ranking/friends" to="/ranking/friends/winnings" />
      <Redirect exact path="/ranking/topscore" to="/ranking/topscore/lastweek" />

      <GuardedRoute exact path="/events" component={EventsRoute} />
      <GuardedRoute exact path="/events/history" component={EventsRoute} />
      <GuardedRoute exact path="/events/:contestId" component={EventRoute} />
      <GuardedRoute exact path="/events/:contestId/rules" component={EventRoute} />

      <GuardedRoute exact path="/ranking/global/winnings" component={LeaderboardRoute} />
      <GuardedRoute exact path="/ranking/global/matches" component={LeaderboardRoute} />
      <GuardedRoute exact path="/ranking/global/wins" component={LeaderboardRoute} />
      <GuardedRoute exact path="/ranking/friends/winnings" component={LeaderboardRoute} />
      <GuardedRoute exact path="/ranking/friends/matches" component={LeaderboardRoute} />
      <GuardedRoute exact path="/ranking/friends/wins" component={LeaderboardRoute} />
      <GuardedRoute exact path="/ranking/topscore/lastweek" component={LeaderboardRoute} />
      <GuardedRoute exact path="/ranking/topscore/lastmonth" component={LeaderboardRoute} />
      <GuardedRoute exact path="/ranking/topscore/overall" component={LeaderboardRoute} />

      <GuardedRoute exact path="/friends" component={FriendsRoute} />
      <GuardedRoute exact path="/friends/invite" component={FriendsRoute} />

      <GuardedRoute exact path="/rules" component={RulesRoute} />
      <GuardedRoute exact path="/welcome" component={WelcomeRoute} />
      <GuardedRoute exact path="/tutorial" component={TutorialRoute} />
      <GuardedRoute exact path="/store" component={StoreRoute} />
      {config.IS_STORE_DISABLED && <Redirect path="/store" to="/games" />}
      <GuardedRoute exact path="/store/premium-accounts" component={PremiumAccountsRoute} />
      <GuardedRoute exact path="/store/premium-accounts/:productId" component={PremiumAccountsRoute} />
      <GuardedRoute exact path="/store/premium-accounts/:productId/buy" component={PremiumAccountsRoute} />
      <GuardedRoute exact path="/store/:productId" component={StoreRoute} />
      <GuardedRoute exact path="/store/:productId/buy" component={StoreRoute} />

      <GuardedRoute exact path="/games" component={GamesRoute} />
      <GuardedRoute exact path="/games/history" component={GamesRoute} />
      <GuardedRoute exact path="/games/:gameId/invitation" component={GamesRoute} />

      <GuardedRoute exact path="/games/create/type" component={SelectGameTypeRoute} />
      <GuardedRoute exact path="/games/create/match" component={SelectMatchRoute} />
      <GuardedRoute exact path="/games/create/team" component={SelectPlayersRoute} />
      <GuardedRoute exact path="/games/create/friends" component={SelectFriendsRoute} />
      <GuardedRoute exact path="/games/create/stake" component={SelectStakeRoute} />
      <GuardedRoute exact path="/games/create/classic" component={SelectRoundRoute} />
      <GuardedRoute exact path="/games/create/classic/team" component={SelectClassicPlayersRoute} />

      <GuardedRoute exact path="/games/:gameId" component={GameRoute} />
      <GuardedRoute exact path="/games/:gameId/invite" component={InviteMoreFriendsRoute} />
      <GuardedRoute exact path="/games/:gameId/team" component={GameRoute} />
      <GuardedRoute exact path="/games/:gameId/quiz" component={GameRoute} />
      <GuardedRoute exact path="/games/:gameId/matches" component={GameRoute} />
      <GuardedRoute exact path="/games/:gameId/timeline" component={GameRoute} />
      <GuardedRoute exact path="/games/:gameId/timeline/:matchId" component={GameRoute} />
      <GuardedRoute exact path="/games/:gameId/sub/:removedPlayerId" component={SubPlayersRoute} />
      <GuardedRoute exact path="/games/:gameId/accept" component={AcceptInviteRoute} />

      <GuardedRoute exact path="/profile" component={ProfileRoute} />
      <GuardedRoute exact path="/profile/achievements" component={ProfileRoute} />
      <GuardedRoute exact path="/profile/settings" component={SettingsRoute} />

      <GuestRoute exact path="/landing" component={LandingRoute} />
      <GuestRoute exact path="/signin" component={SignInRoute} />
      {config.IS_KICKER_LOGIN_ENABED && <GuestRoute exact path="/signin/kicker" component={KickerLandingRoute} />}
      {config.IS_KICKER_LOGIN_ENABED && <GuestRoute exact path="/signin/kicker/auth" component={KickerSignInRoute} />}
      <GuestRoute exact path="/signup" component={SignUpRoute} />
      <ScreenRoute exact path="/resetpassword" component={ResetPasswordRoute} />

      <GuardedRoute path="*" component={NotFoundRoute} />
    </Switch>
  </BaseRouter>
)

export default Routes
